import {useRootDispatch, useRootSelector} from "./useOutlawStore";
import {InterestMethods, RoundingMethods} from "../Configuration/GeneralOptions/OptionConstants";
import Option from "../entities/option";
import util from "../lib/util";
import {getCurrentFirmId, getCurrentUserId} from "../lib/picos/currentLogin";
import * as OptionActions from "../store/actions/optionActions";
import {deleteOption as deleteOptionDep} from "../store/deps/optionDeps";

// Constants for option names
export enum OptionNames {
    // Outlaw Practice
    BILLING_INCREMENT = 'billingIncrement',
    EMAIL_CLIENT = 'paymentEmailClient',
    EMAIL_PAYER = 'paymentEmailPayer',
    EMAIL_SELF = 'paymentEmailSelf',
    EMAIL_SELF_TO = 'paymentEmailSelfTo',
    FONT_FAMILY = 'invoiceFontFamily',
    FONT_SIZE = 'invoiceFontSize',
    FIRM_ADDRESS = 'invoiceFirmAddress',
    FIRM_LETTERHEAD = 'invoiceFirmLetterhead',
    PAID_CONSULTS = 'paidConsults',
    PAID_CONSULT_PRICE = 'paidConsultPrice',
    PAID_CONSULT_DURATION = 'paidConsultDuration',
    PAID_CONSULT_NAME = 'paidConsultName',
    PAID_CONSULT_ACTIVITY = 'paidConsultActivityId',
    LEAD_FOLLOWUP_ACTIVITY = 'leadFollowupActivityId',
    LEAD_INITIAL_CONSULT_USER = 'leadInitialConsultUser',
    USE_CASE_TRUST = 'useCaseTrust',
    INVOICE_DEFAULT_SUMMARY = 'invoiceDefaultSummary',
    INVOICE_NARRATIVE_HEADER = 'invoiceNarrativeHeader',
    INVOICE_TERMS = 'invoiceTerms',
    ENFORCE_ACTIVITY_TYPE = 'enforceActivityType',
    ENFORCE_CATEGORY_SELECTED = 'enforceCategorySelected',
    ENFORCE_TIE_INS_COMPLETE = 'enforceTieInsComplete',
    ROUNDING_METHOD = 'roundingMethod',
    SHOW_HELP = 'showHelp',
    SOUND_FOR_WIN = 'soundForWin',
    SOUND_FOR_TASK_COMPLETE = 'soundForTaskComplete',
    TASK_EMOJIS = 'taskEmojis',

    // Tax & Interest
    INVOICE_TAX_RATE = 'invoiceTaxRate',
    INVOICE_TAX_LABEL = 'invoiceTaxLabel',
    INVOICE_INTEREST_RATE = 'invoiceInterestRate',
    INVOICE_INTEREST_METHOD = 'invoiceInterestMethod',
    INVOICE_INTEREST_GRACE_PERIOD = 'invoiceInterestGracePeriod',

    // Reporting
    PAYMENT_ALLOCATION_METHOD = 'paymentAllocationMethod',
    UTILIZATION_HOLIDAYS_PER_YEAR = 'utilizationHolidaysPerYear',
    UTILIZATION_VACATION_DAYS = 'utilizationVacationDays',

    // Session Replay
    SESSION_REPLAY_OPT_OUT = 'sessionReplayOptOut',

    // Outlaw Payments
    PAYMENT_PORTAL = 'paymentPortal',
    PAYMENT_PORTAL_CC_SELF = 'paymentPortalCcSelf',
    PAYMENT_PORTAL_CC_EMAIL = 'paymentPortalCcEmail',
    PAYMENT_PORTAL_BENEFACTORS_LINK = 'paymentPortalBenefactorsLink',
    PAYMENT_PORTAL_BENEFACTORS_INVOICE = 'paymentPortalBenefactorsInvoice',
}

export enum TypeValues {
    Boolean = 'boolean',
    Numeric = 'numeric',
    String ='string',
}

export type OptionTypes = string | number | boolean;

export type TypeValueMap<T extends OptionTypes> =
    T extends boolean ? TypeValues.Boolean :
    T extends number ? TypeValues.Numeric :
    TypeValues.String;

export type Defaults<T extends OptionTypes> = {
    defaultValue: T,
    type: TypeValueMap<T>,
}

function useOptions() {
    const options = useRootSelector(state => state.options);
    const dispatch = useRootDispatch();

    const getDefaults = <T extends OptionTypes>(label: OptionNames): Defaults<T> => {
        switch (label) {
            case OptionNames.BILLING_INCREMENT:
                return {defaultValue: 1, type: TypeValues.Numeric} as Defaults<T>;
            case OptionNames.EMAIL_CLIENT:
            case OptionNames.EMAIL_PAYER:
            case OptionNames.EMAIL_SELF:
                return {defaultValue: true, type: TypeValues.Boolean} as Defaults<T>;
            case OptionNames.PAYMENT_PORTAL_CC_SELF:
            case OptionNames.PAYMENT_PORTAL_BENEFACTORS_LINK:
                return {defaultValue: true, type: TypeValues.Boolean} as Defaults<T>;
            case OptionNames.EMAIL_SELF_TO:
                return {defaultValue: '', type: TypeValues.String} as Defaults<T>;
            case OptionNames.FONT_FAMILY:
                return {defaultValue: 'Open Sans, sans-serif', type: TypeValues.String} as Defaults<T>;
            case OptionNames.FONT_SIZE:
                return {defaultValue: '10pt', type: TypeValues.String} as Defaults<T>;
            case OptionNames.FIRM_ADDRESS:
                return {defaultValue: null, type: TypeValues.String} as Defaults<T>;
            case OptionNames.FIRM_LETTERHEAD:
                return {defaultValue: 'Set up letterhead in Options/Invoicing', type: TypeValues.String} as Defaults<T>;
            case OptionNames.PAID_CONSULTS:
                return {defaultValue: true, type: TypeValues.Boolean} as Defaults<T>;
            case OptionNames.PAID_CONSULT_PRICE:
                return {defaultValue: 250, type: TypeValues.Numeric} as Defaults<T>;
            case OptionNames.PAID_CONSULT_DURATION:
                return {defaultValue: 1, type: TypeValues.Numeric} as Defaults<T>;
            case OptionNames.PAID_CONSULT_NAME:
                return {defaultValue: 'Initial Consultation', type: TypeValues.String} as Defaults<T>;
            case OptionNames.PAID_CONSULT_ACTIVITY:
                return {defaultValue: 'aea93c81-c896-4116-82f0-99864deef232', type: TypeValues.String} as Defaults<T>;
            case OptionNames.LEAD_FOLLOWUP_ACTIVITY:
                return {defaultValue: '7a87b303-ce41-428e-98b2-bfdca74d2a83', type: TypeValues.String} as Defaults<T>;
            case OptionNames.LEAD_INITIAL_CONSULT_USER:
                return {defaultValue: '', type: TypeValues.String} as Defaults<T>;
            case OptionNames.USE_CASE_TRUST:
                return {defaultValue: true, type: TypeValues.Boolean} as Defaults<T>;
            case OptionNames.INVOICE_DEFAULT_SUMMARY:
                return {defaultValue: '', type: TypeValues.String} as Defaults<T>;
            case OptionNames.INVOICE_NARRATIVE_HEADER:
                return {defaultValue: 'Work We Did for You', type: TypeValues.String} as Defaults<T>;
            case OptionNames.INVOICE_TERMS:
                return {defaultValue: 0, type: TypeValues.Numeric} as Defaults<T>;
            case OptionNames.ENFORCE_ACTIVITY_TYPE:
                return {defaultValue: false, type: TypeValues.Boolean} as Defaults<T>;
            case OptionNames.ENFORCE_CATEGORY_SELECTED:
                return {defaultValue: false, type: TypeValues.Boolean} as Defaults<T>;
            case OptionNames.ENFORCE_TIE_INS_COMPLETE:
                return {defaultValue: true, type: TypeValues.Boolean} as Defaults<T>;
            case OptionNames.TASK_EMOJIS:
                return {defaultValue: false, type: TypeValues.Boolean} as Defaults<T>;
            case OptionNames.ROUNDING_METHOD:
                return {defaultValue: RoundingMethods.ROUNDING_METHOD_MIDDLE, type: TypeValues.String} as Defaults<T>;
            case OptionNames.SHOW_HELP:
                return {defaultValue: true, type: TypeValues.Boolean} as Defaults<T>;
            case OptionNames.SOUND_FOR_TASK_COMPLETE:
            case OptionNames.SOUND_FOR_WIN:
                return {defaultValue: true, type: TypeValues.Boolean} as Defaults<T>;
            case OptionNames.PAYMENT_ALLOCATION_METHOD:
                return {defaultValue: 'proportional', type: TypeValues.String} as Defaults<T>;
            case OptionNames.UTILIZATION_HOLIDAYS_PER_YEAR:
                return {defaultValue: 11, type: TypeValues.Numeric} as Defaults<T>;
            case OptionNames.UTILIZATION_VACATION_DAYS:
                return {defaultValue: 15, type: TypeValues.Numeric} as Defaults<T>;
            case OptionNames.SESSION_REPLAY_OPT_OUT:
                return {defaultValue: false, type: TypeValues.Boolean} as Defaults<T>;
            case OptionNames.PAYMENT_PORTAL:
                return {defaultValue: true, type: TypeValues.Boolean} as Defaults<T>;
            case OptionNames.PAYMENT_PORTAL_CC_EMAIL:
                return {defaultValue: '', type: TypeValues.String} as Defaults<T>;
            case OptionNames.PAYMENT_PORTAL_BENEFACTORS_INVOICE:
                return {defaultValue: false, type: TypeValues.Boolean} as Defaults<T>;
            case OptionNames.INVOICE_TAX_RATE:
                return {defaultValue: 0, type: TypeValues.Numeric} as Defaults<T>;
            case OptionNames.INVOICE_TAX_LABEL:
                return {defaultValue: '', type: TypeValues.String} as Defaults<T>;
            case OptionNames.INVOICE_INTEREST_RATE:
                return {defaultValue: 0, type: TypeValues.Numeric} as Defaults<T>;
            case OptionNames.INVOICE_INTEREST_METHOD:
                return {defaultValue: InterestMethods.SIMPLE_MONTHLY, type: TypeValues.String} as Defaults<T>;
            case OptionNames.INVOICE_INTEREST_GRACE_PERIOD:
                return {defaultValue: 0, type: TypeValues.Numeric} as Defaults<T>;
            default:
                return {defaultValue: '', type: TypeValues.String} as Defaults<T>;
        }
    }

    /**
     * Get an option based on its label.
     *
     * @param {string} label
     * @return {Option}
     */
    const getOption = (label: OptionNames): Option => {
        // Get defaults for this label
        const {defaultValue, type} = getDefaults(label);

        // See if the option already exists
        const result = util.sortEntityArrayByDate(options
            .getMappingFor('label', label), 'addedOn', 'desc')[0];

        if (!result) {
            // See if it exists in defaultEntries section
            return Option.getDefaultEntries().find(o => o.label === label) || new Option({
                optionId: util.genUUID(),
                firmId: getCurrentFirmId(),
                addedBy: getCurrentUserId(),
                addedOn: 0.1,
                status: 'C',
                userScope: false,
                label,
                type,
                value: (defaultValue != null)
                       ? type === 'string'
                         ? defaultValue
                         : type === 'boolean'
                           ? defaultValue === true || defaultValue === 'true' || defaultValue === 't'
                           : `${defaultValue}`
                       : null,

            });
        }
        else return result;
    }

    /**
     * Get the value of the option.
     *
     * @param {string} label
     * @return {string|number|boolean|null}
     */
    const getValue = <T extends OptionTypes>(label: OptionNames): T => {
        try {
            const option = getOption(label);
            const value = option.value;

            return (option.type === 'numeric'
                    ? Number.parseFloat(value)
                    : option.type === 'boolean'
                      ? typeof value === 'string'
                        ? value.toLowerCase().startsWith('t')
                        : value
                      : value) as T;
        }
        catch (err: any) {
            console.error(`useOptions: getValue(${label})`, err.message);
            return null;
        }
    }

    /**
     * Set an option by label/value and optional type.
     * @param {string} label
     * @param {string|number|boolean} value
     * @param {string} [type]
     * @returns {Option}
     */
    const setOption = (label: OptionNames, value: OptionTypes, type: TypeValues = TypeValues.String): Option => {
        // First see if the option exists already
        let option: Option;

        try {
            option = getOption(label);
        }
        catch (err) {
            // Safe to ignore
        }

        if (!option) option = new Option({
            firmId: getCurrentFirmId(),
            optionId: util.genUUID(),
            addedBy: getCurrentUserId(),
            addedOn: util.newHRTimestamp(),
            type, label, value
        });
        else {
            option.value = `${value}`;
        }

        // Now save it
        dispatch(OptionActions.saveOptionQueue(option));

        return option;
    }

    /**
     * Delete an option in its entirety.
     *
     * @param {string} label
     */
    const deleteOption = (label: OptionNames) => {
        const option = getOption(label);

        if (option) deleteOptionDep(option);
    }

    return {getDefaults, getValue, getOption, setOption, deleteOption };
}

export default useOptions;